import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import profesorParticularApiService from '../services/ProfesorParticularApiService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import { useAuth } from '../contexts/AuthContext';
import { isAdminColegio } from '../utils/adminMenu';

const AdminCreateProfesorParticular = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);

  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    dni: '',
    telefono: '',
    cuit: '',
    descripcion: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [particulares, setParticulares] = useState([]);
  const [particularesLoading, setParticularesLoading] = useState(false);
  const [particularesError, setParticularesError] = useState('');

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin/select-colegio', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  useEffect(() => {
    const fetchParticulares = async () => {
      setParticularesLoading(true);
      setParticularesError('');
      try {
        const resp = await profesorParticularApiService.getProfesoresParticulares();
        const list = resp?.profesoresParticulares || resp?.ProfesoresParticulares || resp?.data || resp || [];
        setParticulares(Array.isArray(list) ? list : []);
      } catch (error) {
        setParticularesError(error.message || 'No pudimos cargar los particulares.');
      } finally {
        setParticularesLoading(false);
      }
    };

    if (isGeneralAdmin) {
      fetchParticulares();
    }
  }, [isGeneralAdmin]);

  const formatCuit = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    const parts = [];
    if (digits.length > 0) parts.push(digits.slice(0, 2));
    if (digits.length > 2) parts.push(digits.slice(2, 10));
    if (digits.length > 10) parts.push(digits.slice(10, 11));
    return parts.join('-');
  };

  const handleChange = (field, value) => {
    const nextValue = field === 'cuit' ? formatCuit(value) : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del particular.');
      setMessageType('error');
      return;
    }
    if (!form.email.trim()) {
      setMessage('Completa el email del particular.');
      setMessageType('error');
      return;
    }
    if (!form.dni.trim()) {
      setMessage('Completa el DNI del particular.');
      setMessageType('error');
      return;
    }
    if (form.dni.trim().length < 6) {
      setMessage('El DNI debe tener al menos 6 caracteres.');
      setMessageType('error');
      return;
    }
    if (form.cuit.trim() && !/^\d{2}-\d{8}-\d$/.test(form.cuit.trim())) {
      setMessage('El CUIT debe tener el formato 20-33455684-2.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando particular...');
    setMessageType('info');

    try {
      await profesorParticularApiService.createProfesorParticular({
        nombre: form.nombre,
        apellido: form.apellido,
        email: form.email,
        dni: form.dni,
        telefono: form.telefono,
        cuit: form.cuit,
        descripcion: form.descripcion
      });
      setMessage('Profesor particular creado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin/select-colegio', { replace: true, state: { scope: 'particulares' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el particular.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Administración general</p>
          <h1>Alta particular</h1>
          <p className="subtitle">Crea el profesor particular y su usuario de acceso.</p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveColegioBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a particulares</span>
          </button>
          <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
        </div>
      </header>

      <div className="admin-dual-panel">
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-user-tie" />
              <span>Datos del particular</span>
            </div>
          </div>
          <div className="card-content">
            <p style={{ marginTop: 0, color: '#475569' }}>
              Los campos obligatorios son nombre, email y DNI. El usuario se crea con rol ProfesorParticular.
            </p>
            <form onSubmit={handleSubmit} className="prof-create-form">
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Nombre"
                  />
                </div>
                <div className="form-field">
                  <label>Apellido</label>
                  <input
                    type="text"
                    value={form.apellido}
                    onChange={(e) => handleChange('apellido', e.target.value)}
                    placeholder="Apellido"
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="profesor@email.com"
                  />
                </div>
                <div className="form-field">
                  <label>DNI *</label>
                  <input
                    type="text"
                    value={form.dni}
                    onChange={(e) => handleChange('dni', e.target.value)}
                    placeholder="30111222"
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="351-555-1122"
                  />
                </div>
                <div className="form-field">
                  <label>CUIT</label>
                  <input
                    type="text"
                    value={form.cuit}
                    onChange={(e) => handleChange('cuit', e.target.value)}
                    inputMode="numeric"
                    pattern="\d{2}-\d{8}-\d"
                    placeholder="20-33455684-2"
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Descripción</label>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Especialidad, materias o modalidad de preparación"
                  style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                />
              </div>

              {message && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.5rem',
                    borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {message}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                  <span>Crear particular</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-users" />
              <span>Particulares existentes</span>
            </div>
          </div>
          <div className="card-content">
            {particularesLoading && <p className="state">Cargando particulares...</p>}
            {particularesError && <p className="state error">{particularesError}</p>}
            {!particularesLoading && !particularesError && particulares.length === 0 && (
              <p className="state">Todavía no hay particulares creados.</p>
            )}
            {!particularesLoading && !particularesError && particulares.length > 0 && (
              <div className="admin-list">
                {particulares.map((reg) => (
                  <div key={reg.id || reg.Id || reg.email || reg.Email} className="admin-list-item">
                    <div>
                      <strong>{reg.nombre || reg.Nombre || 'Sin nombre'}</strong>
                      <p style={{ margin: '0.2rem 0 0', color: '#64748b' }}>{reg.email || reg.Email || reg.descripcion || reg.Descripcion || '-'}</p>
                    </div>
                    <span className={`status-chip ${(reg.activo ?? reg.Activo ?? true) ? 'success' : 'danger'}`}>
                      {(reg.activo ?? reg.Activo ?? true) ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateProfesorParticular;
